import { IBatch, IInventoryTransaction, IProductBatch, ISerializedItem } from '@core/models/batch.model';
import React, { FC } from 'react';
import {
    displayValue,
    formatBatchDate,
    formatBatchDateOnly,
    formatBatchPrice,
    formatInventoryStatus,
    productBatchTitle,
    productLineCount,
    serialStatusBadgeClass,
    totalBatchQty,
    totalSerialCount,
    verifiedLineCount,
} from './batch-detail.utils';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-2">
            <span className="min-w-[170px] shrink-0 text-sm font-medium text-slate-500">{label}</span>
            <span className="break-all text-sm text-slate-800">{value ?? '—'}</span>
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

const SerialTable: FC<{ items: ISerializedItem[] }> = ({ items }) => {
    if (items.length === 0) {
        return <p className="text-sm text-slate-500">No serialized units.</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[420px] border-collapse text-sm">
                <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                        <th className="px-2 py-2">#</th>
                        <th className="px-2 py-2">Serial number</th>
                        <th className="px-2 py-2">Status</th>
                        <th className="px-2 py-2">Item ID</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((s, idx) => (
                        <tr key={s.id ?? s.serialNumber ?? idx} className="border-b border-slate-100">
                            <td className="px-2 py-2 text-slate-600">{idx + 1}</td>
                            <td className="px-2 py-2 font-mono text-slate-900">{s.serialNumber || '—'}</td>
                            <td className="px-2 py-2">
                                {s.status ? (
                                    <span
                                        className={`rounded px-2 py-0.5 text-xs font-semibold ${serialStatusBadgeClass(s.status)}`}
                                    >
                                        {formatInventoryStatus(s.status)}
                                    </span>
                                ) : (
                                    '—'
                                )}
                            </td>
                            <td className="px-2 py-2 text-slate-600">{displayValue(s.id)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const InventoryTable: FC<{ items: IInventoryTransaction[] }> = ({ items }) => {
    if (items.length === 0) {
        return <p className="text-sm text-slate-500">No inventory transactions recorded.</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-sm">
                <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                        <th className="px-2 py-2">Date</th>
                        <th className="px-2 py-2">Type</th>
                        <th className="px-2 py-2">Reference</th>
                        <th className="px-2 py-2">Qty</th>
                        <th className="px-2 py-2">External ref</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((tx) => (
                        <tr key={tx.id ?? `${tx.transactionDate}-${tx.type}`} className="border-b border-slate-100">
                            <td className="px-2 py-2 text-slate-700">{formatBatchDate(tx.transactionDate)}</td>
                            <td className="px-2 py-2">{displayValue(tx.type)}</td>
                            <td className="px-2 py-2">{formatInventoryStatus(tx.reference)}</td>
                            <td className="px-2 py-2 font-semibold">{displayValue(tx.quantity)}</td>
                            <td className="px-2 py-2 font-mono text-xs">{displayValue(tx.ExternalReference)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const ProductLineCard: FC<{ pb: IProductBatch; showInventory?: boolean }> = ({ pb, showInventory }) => (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div>
                <h5 className="text-base font-semibold text-slate-900">{productBatchTitle(pb)}</h5>
                <p className="text-xs text-slate-500">Product batch line #{pb.id}</p>
            </div>
            <span
                className={`rounded px-2 py-0.5 text-xs font-semibold ${
                    pb.rxProduct ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-200 text-slate-700'
                }`}
            >
                {pb.rxProduct ? 'RX' : 'OTC'}
            </span>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
            <Section title="Product identity">
                <InfoRow label="Product ID" value={displayValue(pb.productId)} />
                <InfoRow label="Product name" value={displayValue(pb.productName)} />
                <InfoRow label="NDC" value={displayValue(pb.productNdc)} />
                <InfoRow label="GTIN" value={displayValue(pb.productGtin)} />
            </Section>

            <Section title="GS1 / packaging">
                <InfoRow label="Company prefix" value={displayValue(pb.companyPrefix)} />
                <InfoRow label="Item reference" value={displayValue(pb.itemReference)} />
                <InfoRow label="Package barcode" value={displayValue(pb.packageBarcode)} />
            </Section>

            <Section title="Line inventory">
                <InfoRow label="Lot number" value={displayValue(pb.lotNumber)} />
                <InfoRow label="Expiry date" value={formatBatchDateOnly(pb.expiryDate)} />
                <InfoRow label="On-hand quantity" value={displayValue(pb.quantity)} />
                <InfoRow label="Batch size" value={displayValue(pb.batchSize)} />
                <InfoRow label="Actual price" value={formatBatchPrice(pb.actualPrice)} />
                <InfoRow label="Last inventory type" value={formatInventoryStatus(pb.lastInventoryType)} />
            </Section>

            <Section title="Pallet">
                <InfoRow label="Pallet ID" value={displayValue(pb.pallet?.id)} />
                <InfoRow label="SSCC" value={displayValue(pb.pallet?.sscc)} />
                <InfoRow
                    label="Weight (kg)"
                    value={pb.pallet?.weightKg != null ? String(pb.pallet.weightKg) : '—'}
                />
            </Section>

            <Section title="VRS / DSCSA">
                <InfoRow
                    label="Verification"
                    value={
                        <span
                            className={`rounded px-2 py-0.5 text-xs font-semibold ${
                                pb.verify ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-900'
                            }`}
                        >
                            {pb.verify ? 'Verified' : 'Pending'}
                        </span>
                    }
                />
                <InfoRow label="Verifier GLN" value={displayValue(pb.verifierGln)} />
                <InfoRow label="Verification time" value={formatBatchDate(pb.verificationTime)} />
            </Section>
        </div>

        {pb.rxProduct && (
            <div className="mt-4">
                <h6 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Serialized units ({pb.serializedItems?.length ?? 0})
                </h6>
                <SerialTable items={pb.serializedItems ?? []} />
            </div>
        )}

        {showInventory && (
            <div className="mt-4">
                <h6 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Inventory history
                </h6>
                <InventoryTable items={pb.inventoryTransactions ?? []} />
            </div>
        )}
    </div>
);

type Props = {
    batch: IBatch | null;
    loading?: boolean;
    /** Detail page loads inventory transactions; list expand may omit them */
    showInventory?: boolean;
};

const BatchDetailContent: FC<Props> = ({ batch, loading, showInventory = true }) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-16 text-sm text-slate-500">Loading batch data…</div>
        );
    }

    if (!batch) {
        return <div className="py-8 text-center text-sm text-slate-500">Batch not found.</div>;
    }

    const lines = batch.productBatches ?? [];

    return (
        <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Product lines</p>
                    <p className="mt-0.5 text-lg font-semibold text-slate-900">{productLineCount(batch)}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total on-hand qty</p>
                    <p className="mt-0.5 text-lg font-semibold text-slate-900">{totalBatchQty(batch)}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">RX serials</p>
                    <p className="mt-0.5 text-lg font-semibold text-slate-900">{totalSerialCount(batch)}</p>
                </div>
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">VRS verified lines</p>
                    <p className="mt-0.5 text-lg font-semibold text-slate-900">
                        {verifiedLineCount(batch)} / {lines.length || 0}
                    </p>
                </div>
            </div>

            <Section title="Batch overview">
                <InfoRow label="Batch ID" value={displayValue(batch.id)} />
                <InfoRow label="Lot number" value={displayValue(batch.lotNumber)} />
                <InfoRow label="Batch expiry" value={formatBatchDateOnly(batch.expiryDate)} />
                <InfoRow label="Packaging info" value={displayValue(batch.packagingInfo)} />
                <InfoRow label="Purchase order" value={displayValue(batch.purchaseOrderNumber)} />
                <InfoRow label="Warehouse" value={displayValue(batch.warehouseName)} />
                <InfoRow label="Warehouse ID" value={displayValue(batch.warehouseId)} />
                <InfoRow label="Supplier email" value={displayValue(batch.preferredSupplierEmail)} />
                <InfoRow label="Supplier business" value={displayValue(batch.preferredSupplierBusinessName)} />
                <InfoRow label="Supplier ID" value={displayValue(batch.prefferedSupplierId)} />
                <InfoRow label="Created" value={formatBatchDate(batch.createdAt)} />
                <InfoRow label="Created by" value={displayValue(batch.createdBy)} />
                <InfoRow label="Updated" value={formatBatchDate(batch.updatedAt)} />
                <InfoRow label="Updated by" value={displayValue(batch.updatedBy)} />
                {batch.comment && <InfoRow label="Comment" value={batch.comment} />}
            </Section>

            <div>
                <h4 className="mb-3 text-sm font-semibold text-slate-800">Product batches ({lines.length})</h4>
                {lines.length === 0 ? (
                    <p className="text-sm text-slate-500">No product batches in this record.</p>
                ) : (
                    <div className="space-y-4">
                        {lines.map((pb) => (
                            <ProductLineCard key={pb.id} pb={pb} showInventory={showInventory} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BatchDetailContent;
